import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Store, Search, Users, Package, MapPin, Loader2, BadgeCheck } from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const AdminStoresContent = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadStores = useCallback(async () => {
    try {
      const res = await api.get('/marketing/stores', { headers });
      setStores(res.data);
    } catch {
      toast({ title: 'Error cargando tiendas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStores(); }, [loadStores]);

  const toggleStore = async (id, currentActive) => {
    try {
      await api.put(`/marketing/stores/${id}/status`, { is_active: !currentActive }, { headers });
      setStores(prev => prev.map(s => s.id === id ? { ...s, is_active: !currentActive } : s));
      toast({ title: !currentActive ? 'Tienda activada' : 'Tienda suspendida' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const verifyStore = async (id, currentVerified) => {
    try {
      const endpoint = currentVerified ? `/admin/stores/${id}/unverify` : `/admin/stores/${id}/verify`;
      await api.put(endpoint, {}, { headers });
      setStores(prev => prev.map(s => s.id === id ? { ...s, is_verified: !currentVerified } : s));
      toast({ title: !currentVerified ? 'Tienda verificada' : 'Verificacion removida' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const filtered = filter === 'all' ? stores : stores.filter(s => filter === 'active' ? s.is_active : !s.is_active);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-stores-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Store className="w-6 h-6 text-orange-400" /> Tiendas</h1>
          <p className="text-gray-400 text-sm mt-1">{stores.length} tiendas registradas</p>
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'suspended'].map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)}
              className={filter === f ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300'}>
              {f === 'all' ? 'Todas' : f === 'active' ? 'Activas' : 'Suspendidas'}
            </Button>
          ))}
        </div>
      </div>

      <Card className="bg-gray-800 border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stores-table">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-900/50">
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Tienda</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Vendedor</th>
                <th className="text-center py-3 px-4 text-gray-400 font-medium">Productos</th>
                <th className="text-center py-3 px-4 text-gray-400 font-medium">Ventas</th>
                <th className="text-center py-3 px-4 text-gray-400 font-medium">Ubicacion</th>
                <th className="text-center py-3 px-4 text-gray-400 font-medium">Verificada</th>
                <th className="text-center py-3 px-4 text-gray-400 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(store => {
                const sc = store.store_config || {};
                const coords = sc.location?.coordinates;
                return (
                  <tr key={store.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <Store className="w-4 h-4 text-orange-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium flex items-center gap-1.5">
                            {sc.store_name || 'Sin nombre'}
                            {store.is_verified && <BadgeCheck className="w-4 h-4 text-blue-400" />}
                          </p>
                          <p className="text-xs text-gray-500">{store.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-300">{store.name}</td>
                    <td className="py-3 px-4 text-center text-orange-400 font-bold">{store.products_count || 0}</td>
                    <td className="py-3 px-4 text-center text-gray-300">{store.sales || 0}</td>
                    <td className="py-3 px-4 text-center">
                      {coords && coords[0] !== 0 ? (
                        <span className="text-green-400 text-xs flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> Si</span>
                      ) : (
                        <span className="text-gray-500 text-xs">No</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => verifyStore(store.id, !!store.is_verified)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          store.is_verified
                            ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                        }`}
                        data-testid={`verify-btn-${store.id}`}
                      >
                        {store.is_verified ? 'Verificada' : 'Verificar'}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Switch
                        checked={store.is_active !== false}
                        onCheckedChange={() => toggleStore(store.id, store.is_active !== false)}
                        data-testid={`store-toggle-${store.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-gray-500">No hay tiendas</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const AdminStores = () => (
  <AdminLayout>
    <AdminStoresContent />
  </AdminLayout>
);

export default AdminStores;
